import { apiGet, APIURL, appendFlatpickrBaseStyles, cancelSelectEvent, changeSymbolForWidget, defaultIntervalOptions, getCSSStyles, getHeaderPinButtonHTML, getHeaderPinStateClass, getIntervalAndSubpointsFromPeriod, getNoDataHTML, getStartDateFromPeriod, getSymbolDropdown, getTraderSMCardHTML, getWidgetDataURI, isHeaderPinned, normalizeWidgetTheme, periodDefaultValue, setupHeaderPin, toggleWidgetTheme , chartWindowStart, chartWindowEnd } from './../../helpers';
import { CandlestickChartConfig, WidgetEvent, WidgetEventHandler, WidgetEventType } from './../../types';

interface PricePoint {
  timestamp: string;
  close: number;
  open: number;
  high: number;
  low: number;
  volume: number;
}

export class TraderversePriceHistory {
  private container: HTMLElement;
  private config: CandlestickChartConfig;
  private eventHandlers: Map<WidgetEventType, WidgetEventHandler[]> = new Map();
  private updateInterval: number | null = null;
  private shadowRoot: ShadowRoot;
  private currentData: PricePoint[] | null = null;
  private widgetResizeObserver: ResizeObserver | null = null;
  private apiHost: string = `${APIURL}/v3/chartapi`;

  constructor(config: CandlestickChartConfig) {
    this.config = { ...this.getDefaultConfig(), ...config };
    this.config.theme = normalizeWidgetTheme(this.config.theme);
    this.apiHost = this.config.apiHost ?? this.apiHost;
    this.container = this.resolveContainer(config.container);

    if ((this.container as any).shadowRoot) {
      this.shadowRoot = (this.container as any).shadowRoot;
    } else {
      this.shadowRoot = this.container.attachShadow({ mode: 'closed' });
    }
    this.init();

    this.widgetResizeObserver = new ResizeObserver(() => {
      this.updateDisplay();
    });
    setTimeout(() => {
      const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
      if (widget) this.widgetResizeObserver?.observe(widget);
    }, 0);
  }

  private fetchSymbols(): Array<{ value: string; label: string }> {
    return [
      { label: 'AAPL', value: 'AAPL' }, { label: 'MSFT', value: 'MSFT' },
      { label: 'GOOGL', value: 'GOOGL' }, { label: 'AMZN', value: 'AMZN' },
      { label: 'NVDA', value: 'NVDA' }, { label: 'META', value: 'META' },
      { label: 'TSLA', value: 'TSLA' }, { label: 'AVGO', value: 'AVGO' },
      { label: 'PEP', value: 'PEP' }, { label: 'COST', value: 'COST' },
    ];
  }

  private getDefaultConfig(): Partial<CandlestickChartConfig> {
    const symbols = this.fetchSymbols();
    return {
      headerPinned: true,
      symbol: `${symbols[0].value}_NASDAQ`,
      theme: 'default-dark',
      width: '100%',
      height: '400px',
      autosize: true,
      period: '1M',
      interval: '1D',
      dataPoint: 732,
    };
  }

  private resolveContainer(container: string | HTMLElement): HTMLElement {
    if (typeof container === 'string') {
      const el = document.getElementById(container) || document.querySelector(container);
      if (!el) throw new Error(`Container not found: ${container}`);
      return el as HTMLElement;
    }
    return container;
  }

  private normalizeSymbol(symbol: string): string {
    if (!symbol) return `${this.fetchSymbols()[0].value}_NASDAQ`;
    return symbol.includes('_') ? symbol : `${symbol}_NASDAQ`;
  }

  private async init(): Promise<void> {
    this.config.symbol = this.normalizeSymbol(this.config.symbol || this.fetchSymbols()[0].value);

    this.createWidget();
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');
    await this.attachStyles();
    this.setupEventListeners();
    this.renderDropdownOptions();
    await this.setupChart(this.config.symbol!);
    if (widget) widget.classList.remove('loading');

    this.emit('ready', {});
    this.config.onReady?.();
    cancelSelectEvent(this.shadowRoot);
  }

  private filtersIconSvg(): string {
    return `<svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M12.1794 0.950592H1.72077C1.57285 0.950469 1.42802 0.991908 1.30363 1.06995C1.17925 1.14799 1.08058 1.25932 1.01944 1.39061C0.958302 1.5219 0.937286 1.66759 0.958909 1.81023C0.980533 1.95286 1.04388 2.0864 1.14136 2.19484L5.22108 6.73834C5.34378 6.8752 5.41132 7.05084 5.41114 7.23259V10.8881C5.41114 10.9463 5.42504 11.0037 5.45175 11.0558C5.47846 11.1079 5.51725 11.1532 5.56503 11.1881L7.87343 12.8756C7.93059 12.9174 7.99856 12.9428 8.06972 12.9491C8.14088 12.9553 8.21242 12.9421 8.27633 12.911C8.34023 12.8799 8.39398 12.832 8.43154 12.7727C8.46911 12.7135 8.489 12.6452 8.489 12.5756V7.23259C8.48882 7.05084 8.55636 6.8752 8.67906 6.73834L12.7588 2.19409C13.1943 1.70959 12.8403 0.950592 12.1794 0.950592Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/> </svg>`;
  }

  private createWidget(): void {
    const periodOptions = ['1D', '5D', '1M', '3M', '6M', '1Y', '5Y']
      .map((s) => {
        const selected = this.config.period === s ? 'selected' : '';
        return `<option value="${s}" ${selected}>${s}</option>`;
      })
      .join('');

    const showPeriod = !this.config.hidePeriod;
    const showInterval = !this.config.hideInterval;
    const showFiltersUi = showPeriod || showInterval;
    const filtersSectionHtml = showFiltersUi
      ? `
            <div class="filtersBox sideBar hidden">
              ${showPeriod ? `
              <div class="item">
                <select class="period-dropdown">
                  ${periodOptions}
                </select>
              </div>` : ''}
              ${showInterval ? `
              <div class="item">
                <select class="interval-dropdown">
                </select>
              </div>` : ''}
            </div>`
      : '';

    let themeClass = 'defaultDarkMode';
    let dataTheme = 'default-dark';

    if (this.config.theme === 'white') {
      themeClass = 'whiteMode';
      dataTheme = 'white';
    } else if (this.config.theme === 'black') {
      themeClass = 'blackMode';
      dataTheme = 'black';
    } else if (this.config.theme === 'default-lite') {
      themeClass = 'defaultLightMode';
      dataTheme = 'default-lite';
    }

    const symbolLabel = this.config.symbol?.split('_')[0] ?? this.fetchSymbols()[0].value;

    const widgetHtml = `
      <div style="width: ${typeof this.config.width == 'number' ? this.config.width + 'px' : this.config.width};height: ${typeof this.config.height == 'number' ? this.config.height + 'px' : this.config.height};" class="widgetBox ${themeClass} loading ${getHeaderPinStateClass(this.config)}" data-theme="${dataTheme}">
        <div class="headTop">
          <h4 class="title stock">${this.getTitleIconSvg()} Price History</h4>
          <div class="actionCol">${getHeaderPinButtonHTML(this.config)}
            ${showFiltersUi ? `
            <div class="item">
              <button class="filters-toggle" type="button">${this.filtersIconSvg()}</button>
            </div>` : ''}
            <i class="pi pi-ellipsis-v"></i>
          </div>
        </div>

        <div class="inBody">
          <div class="optionsRow">
            ${this.config.hideSymbol ? '<div class="item dropStyle01"></div>' : `<div class="item dropStyle01">
              <div class="trader-sm-card" id="selected-symbol">
                ${getTraderSMCardHTML(this.config.stock ?? { symbol: symbolLabel, type: 'stock', name: '' })}
              </div>
              <span id="symbol-dropdown-placeholder"></span>
            </div>`}
            <span></span>
            ${filtersSectionHtml}
          </div>
          <div class="chartDv" id="eventsContainer" style="height: 100%; padding: 0; margin: 0;"></div>
        </div>
      </div>
    `;

    this.shadowRoot.innerHTML = widgetHtml;

    if (this.config.isSymbolSelectable && !this.config.hideSymbol) {
      const dropdown = getSymbolDropdown((data: any) => this.onSelectChange(data));
      const placeholder = this.shadowRoot.getElementById('symbol-dropdown-placeholder');
      if (placeholder && dropdown) {
        placeholder.replaceWith(dropdown);
      }
    }
  }

  onSelectChange(stock: any) {
    this.changeSymbol(stock.stockId ?? stock.symbol, stock);
  }

  private changeSymbol(stockId: string, stock: any = null): void {
    const normalized = this.normalizeSymbol(stockId);
    changeSymbolForWidget(
      this.shadowRoot,
      this.config,
      stock,
      normalized,
      (id: string) => this.setupChart(id),
      (type: any, data: any) => this.emit(type, data),
    );
  }

  private renderDropdownOptions(): void {
    const intervalDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
    const configuredPeriod = this.config.period as keyof typeof defaultIntervalOptions;
    const periodKey: keyof typeof defaultIntervalOptions =
      configuredPeriod && configuredPeriod in defaultIntervalOptions ? configuredPeriod : '1M';
    const intervals = defaultIntervalOptions[periodKey];
    const intervalOptions = intervals
      .map((f) => {
        const selected = this.config.interval === f ? 'selected' : '';
        return `<option value="${f}" ${selected}>${f}</option>`;
      })
      .join('');
    if (intervalDropdown) {
      intervalDropdown.innerHTML = intervalOptions;
    }
  }

  private setupEventListeners(): void {
    setupHeaderPin(this.shadowRoot, this.config);
    const filtersToggle = this.shadowRoot.querySelector('.filters-toggle') as HTMLButtonElement | null;
    const filtersBox = this.shadowRoot.querySelector('.filtersBox') as HTMLElement | null;
    if (filtersToggle && filtersBox) {
      filtersToggle.addEventListener('click', (ev: Event) => {
        ev.preventDefault();
        filtersBox.classList.toggle('hidden');
      });
    }

    const periodDropdown = this.shadowRoot.querySelector('.period-dropdown') as HTMLSelectElement;
    periodDropdown?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.period = value;
      this.config.interval = periodDefaultValue[value as keyof typeof periodDefaultValue];
      this.renderDropdownOptions();
      this.config.onPeriodChange?.(value);
      this.setupChart(this.config.symbol!);
    });
    if (periodDropdown) {
      periodDropdown.value = this.config.period || '1M';
    }

    const frequencyDropdown = this.shadowRoot.querySelector('.interval-dropdown') as HTMLSelectElement;
    frequencyDropdown?.addEventListener('change', (e) => {
      const value = (e.target as HTMLSelectElement).value;
      this.config.interval = value;
      this.config.onIntervalChange?.(value);
      this.renderDropdownOptions();
      this.setupChart(this.config.symbol!);
    });
    if (frequencyDropdown) {
      frequencyDropdown.value = this.config.interval || '1D';
    }
  }

  private async attachStyles(): Promise<void> {
    try {
      appendFlatpickrBaseStyles(this.shadowRoot);
      const styleEl = await getCSSStyles();
      this.shadowRoot.appendChild(styleEl);
    } catch (err) {
      console.error('Failed to load CSS file:', err);
    }
  }

  private async setupChart(symbol: string): Promise<void> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    if (widget) widget.classList.add('loading');

    try {
      const nowUTC = new Date();
      const startDate = getStartDateFromPeriod(this.config.period || '1M');
      const endDate = new Date(Date.UTC(
        nowUTC.getUTCFullYear(),
        nowUTC.getUTCMonth(),
        nowUTC.getUTCDate(),
        23, 59, 59, 999,
      ));

      const { interval, subPoints } = getIntervalAndSubpointsFromPeriod(this.config.interval || '1D');
      const effectiveStartDate =
        interval === 'minute'
          ? new Date(startDate.getTime() - 24 * 60 * 60 * 1000)
          : startDate;

      const normalizedSymbol = this.normalizeSymbol(symbol);
      this.config.symbol = normalizedSymbol;

      const response = await apiGet({
        url: `${this.apiHost}/prices`,
        query: {
          symbol: normalizedSymbol,
          interval,
          sub_points: subPoints,
          start_date: chartWindowStart(effectiveStartDate),
          end_date: chartWindowEnd(endDate),
          data_point: this.config.dataPoint ?? 732,
        },
      });

      if (!response.ok) throw new Error('Failed to fetch data');
      const payload = await response.json();

      this.currentData = (Array.isArray(payload?.data) ? payload.data : [])
        .map((item: any) => ({
          timestamp: item.timestamp,
          open: Number(item.open) || 0,
          high: Number(item.high) || 0,
          low: Number(item.low) || 0,
          close: Number(item.close) || 0,
          volume: Number(item.volume) || 0,
        }))
        .filter((item: PricePoint) => {
          const day = new Date(item.timestamp).getDay();
          return day !== 0 && day !== 6;
        })
        .reverse();

      this.updateDisplay();
      this.emit('dataUpdate', this.currentData);
    } catch (err) {
      console.error('Error fetching price history:', err);
      this.currentData = [];
      this.updateDisplay();
    } finally {
      if (widget) widget.classList.remove('loading');
    }
  }

  private formatDateLabel(dateStr: string): string {
    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) return dateStr;
    const interval = this.config.interval || '1D';
    if (interval.includes('m') || interval.includes('h')) {
      return date.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  }

  private formatCurrency(val: number): string {
    return `$${Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private formatVolume(val: number): string {
    return Number(val).toLocaleString(undefined, { maximumFractionDigits: 0 });
  }

  private formatSignedCurrency(val: number): string {
    const sign = val > 0 ? '+' : '';
    return `${sign}$${Math.abs(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }

  private formatSignedPercent(val: number): string {
    const sign = val > 0 ? '+' : '';
    return `${sign}${val.toFixed(2)}%`;
  }

  private renderPriceHistoryTable(): void {
    const container = this.shadowRoot.querySelector('#eventsContainer');
    const chartDv = this.shadowRoot.querySelector('.chartDv') as HTMLElement;
    if (!container || !chartDv) return;

    const rows = this.currentData ?? [];
    const hasData = rows.length > 0;

    if (hasData) {
      chartDv.setAttribute('data-testing-id', 'Price History');
    } else {
      chartDv.removeAttribute('data-testing-id');
    }

    if (!hasData) {
      container.innerHTML = getNoDataHTML();
      return;
    }

    const displayRows = [...rows].reverse();

    let html = `
      <div class="eventsMain customGrid shareStructure--" style="--grid-template-columns: minmax(150px,1.4fr) minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(100px,1fr) minmax(110px,1fr) minmax(110px,1fr) minmax(140px,1.3fr); --grid-column-gap: 14px; min-width: 1060px;">
        <div class="item header">
          <p>Date</p>
          <p class="cell-end">Open</p>
          <p class="cell-end">High</p>
          <p class="cell-end">Low</p>
          <p class="cell-end">Close</p>
          <p class="cell-end">Change</p>
          <p class="cell-end">Change %</p>
          <p class="cell-end">Volume</p>
        </div>
    `;

    for (let i = 0; i < displayRows.length; i++) {
      const row = displayRows[i];
      const prevClose = i < displayRows.length - 1 ? displayRows[i + 1].close : null;
      const change = prevClose != null ? row.close - prevClose : null;
      const changePct = prevClose != null && prevClose !== 0 ? ((row.close - prevClose) / prevClose) * 100 : null;
      const changeClass = change != null && change < 0 ? 'down' : 'up';

      html += `
        <div class="item body">
          <p>${this.formatDateLabel(row.timestamp)}</p>
          <p class="cell-end">${this.formatCurrency(row.open)}</p>
          <p class="cell-end">${this.formatCurrency(row.high)}</p>
          <p class="cell-end">${this.formatCurrency(row.low)}</p>
          <p class="cell-end">${this.formatCurrency(row.close)}</p>
          <p class="stat cell-end ${change != null ? changeClass : ''}"><span>${change != null ? this.formatSignedCurrency(change) : '-'}</span></p>
          <p class="stat cell-end ${changePct != null ? changeClass : ''}"><span>${changePct != null ? this.formatSignedPercent(changePct) : '-'}</span></p>
          <p class="cell-end">${this.formatVolume(row.volume)}</p>
        </div>`;
    }

    html += `</div>`;
    container.innerHTML = html;
  }

  private updateDisplay(): void {
    if (this.currentData === null) {
      setTimeout(() => this.updateDisplay(), 100);
      return;
    }
    this.renderPriceHistoryTable();
  }

  public on(type: WidgetEventType, handler: WidgetEventHandler): void {
    if (!this.eventHandlers.has(type)) this.eventHandlers.set(type, []);
    this.eventHandlers.get(type)!.push(handler);
  }

  public off(type: WidgetEventType, handler: WidgetEventHandler): void {
    const list = this.eventHandlers.get(type);
    if (list) this.eventHandlers.set(type, list.filter((h) => h !== handler));
  }

  public setSymbol(symbol: string): void {
    this.changeSymbol(symbol);
    const dropdown = this.shadowRoot.querySelector('.symbol-dropdown') as HTMLSelectElement;
    if (dropdown) dropdown.value = symbol;
  }

  public async getWidgetImage(): Promise<string> {
    const widget = this.shadowRoot.querySelector('.widgetBox') as HTMLElement;
    return await getWidgetDataURI(widget);
  }

  public setTheme(theme: 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite'): void {
    if (!this.shadowRoot) return;

    this.config.theme = toggleWidgetTheme(
      this.shadowRoot,
      theme,
    ) as 'white' | 'black' | 'default-dark' | 'default-lite' | 'default-lite';

    this.updateDisplay();
  }

  public destroy(): void {
    if (this.updateInterval) { clearInterval(this.updateInterval); this.updateInterval = null; }
    if (this.widgetResizeObserver) { this.widgetResizeObserver.disconnect(); this.widgetResizeObserver = null; }
    this.eventHandlers.clear();
    this.shadowRoot.innerHTML = '';
  }

  private emit(type: WidgetEventType, data: any): void {
    const handlers = this.eventHandlers.get(type);
    if (handlers) { const evt: WidgetEvent = { type, data }; handlers.forEach((h) => h(evt)); }
  }

  private getTitleIconSvg(): string {
    return `<svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg"> <path d="M8.59998 23.625C8.08748 23.625 7.66248 23.2 7.66248 22.6875V20.1C7.66248 19.5875 8.08748 19.1625 8.59998 19.1625C9.11248 19.1625 9.53748 19.5875 9.53748 20.1V22.6875C9.53748 23.2125 9.11248 23.625 8.59998 23.625Z" fill="currentColor"/> <path d="M15 23.625C14.4875 23.625 14.0625 23.2 14.0625 22.6875V17.5C14.0625 16.9875 14.4875 16.5625 15 16.5625C15.5125 16.5625 15.9375 16.9875 15.9375 17.5V22.6875C15.9375 23.2125 15.5125 23.625 15 23.625Z" fill="currentColor"/> <path d="M21.4 23.625C20.8875 23.625 20.4625 23.2 20.4625 22.6875V14.9125C20.4625 14.4 20.8875 13.975 21.4 13.975C21.9125 13.975 22.3375 14.4 22.3375 14.9125V22.6875C22.3375 23.2125 21.925 23.625 21.4 23.625Z" fill="currentColor"/> <path d="M8.59997 16.475C8.17497 16.475 7.79997 16.1875 7.68747 15.7625C7.56247 15.2625 7.86247 14.75 8.37497 14.625C12.975 13.475 17.025 10.9625 20.1125 7.37502L20.6875 6.70002C21.025 6.31252 21.6125 6.26252 22.0125 6.60002C22.4 6.93752 22.45 7.52502 22.1125 7.92502L21.5375 8.60002C18.2 12.5 13.8 15.2125 8.82497 16.45C8.74997 16.475 8.67497 16.475 8.59997 16.475Z" fill="currentColor"/> <path d="M21.4001 11.9C20.8876 11.9 20.4626 11.475 20.4626 10.9625V8.25H17.7375C17.225 8.25 16.8 7.825 16.8 7.3125C16.8 6.8 17.225 6.375 17.7375 6.375H21.4001C21.9126 6.375 22.3376 6.8 22.3376 7.3125V10.975C22.3376 11.4875 21.9251 11.9 21.4001 11.9Z" fill="currentColor"/> <path d="M18.75 28.4375H11.25C4.4625 28.4375 1.5625 25.5375 1.5625 18.75V11.25C1.5625 4.4625 4.4625 1.5625 11.25 1.5625H18.75C25.5375 1.5625 28.4375 4.4625 28.4375 11.25V18.75C28.4375 25.5375 25.5375 28.4375 18.75 28.4375ZM11.25 3.4375C5.4875 3.4375 3.4375 5.4875 3.4375 11.25V18.75C3.4375 24.5125 5.4875 26.5625 11.25 26.5625H18.75C24.5125 26.5625 26.5625 24.5125 26.5625 18.75V11.25C26.5625 5.4875 24.5125 3.4375 18.75 3.4375H11.25Z" fill="currentColor"/> </svg>`;
  }
}

declare global { interface Window { TraderversePriceHistory: typeof TraderversePriceHistory; } }
if (typeof window !== 'undefined') { window.TraderversePriceHistory = TraderversePriceHistory; }
